import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Cpu, Loader2, Trash2, Check, Plus, Sparkles, Search, Pencil, X, GitMerge, BarChart3, Crown } from "lucide-react";

export const EcuCatalog = () => {
  const [rows, setRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [onlyPending, setOnlyPending] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [nv, setNv] = useState({ brand: "", hw: "" });
  const [stats, setStats] = useState([]);
  const [dupGroups, setDupGroups] = useState([]);
  const [showDups, setShowDups] = useState(false);
  const [keepers, setKeepers] = useState({}); // hkey -> keeper id
  const [merging, setMerging] = useState("");

  const loadExtras = () => {
    api.get("/admin/ecu-catalog/stats").then(({ data }) => setStats(data.top || [])).catch(() => {});
    api.get("/admin/ecu-catalog/duplicates").then(({ data }) => {
      setDupGroups(data.groups || []);
      setKeepers((prev) => {
        const k = { ...prev };
        (data.groups || []).forEach((g) => { if (!k[g.hkey]) k[g.hkey] = g.entries[0]?.id; });
        return k;
      });
    }).catch(() => {});
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/ecu-catalog", { params: { pending: onlyPending ? 1 : 0, q } })
      .then(({ data }) => { setRows(data.ecus); setPendingCount(data.pending); })
      .finally(() => setLoading(false));
    loadExtras();
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [onlyPending, q]);

  const mergeGroup = async (g) => {
    const keepId = keepers[g.hkey] || g.entries[0].id;
    const dropIds = g.entries.map((e) => e.id).filter((id) => id !== keepId);
    if (dropIds.length === 0) return;
    setMerging(g.hkey);
    try {
      await api.post("/admin/ecu-catalog/merge", { keep_id: keepId, drop_ids: dropIds });
      toast.success(`${dropIds.length} doublon(s) fusionné(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setMerging(""); }
  };

  const add = async () => {
    if (!nv.brand.trim() || !nv.hw.trim()) return toast.error("Marque et type ECU requis");
    setBusy(true);
    try {
      await api.post("/admin/ecu-catalog", nv);
      toast.success("ECU enregistré");
      setNv({ brand: "", hw: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const validate = async (id) => { await api.post(`/admin/ecu-catalog/${id}/validate`); toast.success("Validé"); load(); };
  const del = async (id) => { if (!window.confirm("Supprimer cette entrée ?")) return; await api.delete(`/admin/ecu-catalog/${id}`); toast.success("Supprimé"); load(); };

  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState({ brand: "", hw: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const startEdit = (v) => { setEditId(v.id); setEditRow({ brand: v.brand || "", hw: v.hw || "" }); };
  const cancelEdit = () => { setEditId(null); };
  const saveEdit = async (validateToo = false) => {
    if (!editRow.brand.trim() || !editRow.hw.trim()) return toast.error("Marque et type ECU requis");
    setSavingEdit(true);
    try {
      await api.patch(`/admin/ecu-catalog/${editId}`, { ...editRow, validate: validateToo });
      toast.success(validateToo ? "Corrigé & validé ✅" : "Modifications enregistrées ✅");
      setEditId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSavingEdit(false); }
  };

  const inp = "bg-black/50 border border-white/10 text-white px-3 py-2 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none placeholder:text-zinc-600";
  const badge = (v) => v.source === "seed" ? "Préchargé" : v.source === "admin" ? "Admin" : `Appris ×${v.count || 1}`;

  return (
    <div className="glass rounded-md p-6 mb-6" data-testid="ecu-catalog">
      <div className="flex items-center gap-2 mb-2">
        <Cpu className="w-5 h-5 text-aurora-cyan" />
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Catalogue ECU</h3>
        {pendingCount > 0 && (
          <button onClick={() => setOnlyPending((v) => !v)} data-testid="ecu-pending-toggle"
            className={`ml-auto flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-sm transition-colors ${onlyPending ? "bg-signal-processing text-black" : "bg-signal-processing/15 text-signal-processing"}`}>
            <Sparkles className="w-3 h-3" /> {pendingCount} à valider
          </button>
        )}
        {dupGroups.length > 0 && (
          <button onClick={() => setShowDups((v) => !v)} data-testid="ecu-dups-toggle"
            className={`${pendingCount > 0 ? "" : "ml-auto"} flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-sm transition-colors ${showDups ? "bg-aurora-purple text-black" : "bg-aurora-purple/15 text-aurora-purple"}`}>
            <GitMerge className="w-3 h-3" /> {dupGroups.length} doublon(s)
          </button>
        )}
      </div>
      <p className="font-mono text-[10px] text-zinc-500 mb-4 leading-snug">
        Marques → types ECU proposés au dépôt. Le catalogue <b className="text-aurora-cyan">s'enrichit automatiquement</b> : une paire saisie 3 fois par les clients est validée et ajoutée. Validez, corrigez ou supprimez les entrées apprises.
      </p>

      {/* Top ECU by real orders */}
      {stats.length > 0 && (
        <div className="mb-5 p-4 rounded-sm border border-white/10 bg-black/20" data-testid="ecu-stats">
          <div className="flex items-center gap-2 mb-3 font-mono text-[10px] uppercase tracking-widest text-zinc-400"><BarChart3 className="w-3.5 h-3.5 text-aurora-cyan" /> Top ECU les plus traités</div>
          <div className="space-y-1.5">
            {stats.map((s, i) => {
              const max = stats[0].orders || 1;
              return (
                <div key={`${s.brand}-${s.hw}`} className="flex items-center gap-2" data-testid={`ecu-stat-${i}`}>
                  {i === 0 ? <Crown className="w-3.5 h-3.5 text-[#FFB020] shrink-0" /> : <span className="w-3.5 shrink-0 text-center font-mono text-[10px] text-zinc-600">{i + 1}</span>}
                  <span className="text-sm text-white font-medium truncate w-40">{s.brand} <span className="text-aurora-cyan font-mono text-xs">{s.hw}</span></span>
                  <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-gradient-to-r from-aurora-cyan to-aurora-purple" style={{ width: `${Math.max(6, (s.orders / max) * 100)}%` }} /></div>
                  <span className="font-mono text-[10px] text-zinc-400 w-14 text-right">{s.orders} cde{s.orders > 1 ? "s" : ""}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Duplicate merge panel */}
      {showDups && dupGroups.length > 0 && (
        <div className="mb-5 p-4 rounded-sm border border-aurora-purple/30 bg-aurora-purple/5" data-testid="ecu-dups-panel">
          <div className="font-mono text-[10px] uppercase tracking-widest text-aurora-purple mb-3">Doublons potentiels — choisissez l'entrée à conserver puis fusionnez</div>
          <div className="space-y-3">
            {dupGroups.map((g) => (
              <div key={g.hkey} className="p-3 rounded-sm bg-black/30 border border-white/10" data-testid={`ecu-dup-group-${g.hkey}`}>
                <div className="flex flex-wrap gap-2 mb-2">
                  {g.entries.map((e) => (
                    <label key={e.id} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-sm border cursor-pointer text-sm transition-colors ${keepers[g.hkey] === e.id ? "border-signal-success/60 bg-signal-success/10 text-white" : "border-white/15 text-zinc-300 hover:border-white/30"}`}>
                      <input type="radio" name={`keep-${g.hkey}`} checked={keepers[g.hkey] === e.id} onChange={() => setKeepers((k) => ({ ...k, [g.hkey]: e.id }))} data-testid={`ecu-dup-keep-${e.id}`} className="accent-signal-success" />
                      {e.brand} <span className="font-mono text-xs text-aurora-cyan">{e.hw}</span> <span className="font-mono text-[9px] text-zinc-500">×{e.count || 1}</span>
                    </label>
                  ))}
                </div>
                <button onClick={() => mergeGroup(g)} disabled={merging === g.hkey} data-testid={`ecu-dup-merge-${g.hkey}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-aurora-purple text-black font-display font-bold uppercase tracking-wider text-[10px] rounded-sm hover:bg-purple-300 transition-colors disabled:opacity-60">
                  {merging === g.hkey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitMerge className="w-3.5 h-3.5" />} Fusionner
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-2 mb-3" data-testid="ecu-add-form">
        <input className={inp} placeholder="Marque ECU * (Bosch...)" value={nv.brand} onChange={(e) => setNv({ ...nv, brand: e.target.value })} data-testid="ecu-brand" />
        <input className={inp} placeholder="Type ECU * (EDC17C50...)" value={nv.hw} onChange={(e) => setNv({ ...nv, hw: e.target.value })} data-testid="ecu-hw" />
        <button onClick={add} disabled={busy} data-testid="ecu-add-btn"
          className="flex items-center justify-center gap-1.5 px-4 py-2 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-xs rounded-sm hover:bg-cyan-300 transition-colors shadow-glow disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter
        </button>
      </div>

      <div className="relative mb-3">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input className={`${inp} w-full pl-9`} placeholder="Rechercher (marque, type ECU)..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="ecu-search" />
      </div>

      {loading ? <div className="font-mono text-xs text-zinc-500 py-6 text-center">Chargement...</div> : (
        <div className="border border-white/10 rounded-sm overflow-hidden max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-ink-base/95 backdrop-blur">
              <tr className="text-left font-mono text-[9px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                <th className="px-3 py-2">Marque ECU</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Utilisations</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                editId === v.id ? (
                  <tr key={v.id} data-testid={`ecu-row-${v.id}`} className="border-b border-white/5 bg-aurora-cyan/5">
                    <td className="px-2 py-2"><input className={`${inp} w-full`} value={editRow.brand} onChange={(e) => setEditRow({ ...editRow, brand: e.target.value })} data-testid={`ecu-edit-brand-${v.id}`} placeholder="Marque *" /></td>
                    <td className="px-2 py-2"><input className={`${inp} w-full`} value={editRow.hw} onChange={(e) => setEditRow({ ...editRow, hw: e.target.value })} data-testid={`ecu-edit-hw-${v.id}`} placeholder="Type *" /></td>
                    <td className="px-3 py-2 text-zinc-500 font-mono text-xs">{v.count || 1}</td>
                    <td className="px-3 py-2" />
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1.5">
                        {!v.validated && (
                          <button onClick={() => saveEdit(true)} disabled={savingEdit} data-testid={`ecu-save-validate-${v.id}`} title="Corriger & valider"
                            className="flex items-center gap-1 px-2 h-7 border border-signal-success/40 text-signal-success rounded-sm hover:bg-signal-success/10 transition-colors font-mono text-[9px] uppercase tracking-widest">
                            {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Valider
                          </button>
                        )}
                        <button onClick={() => saveEdit(false)} disabled={savingEdit} data-testid={`ecu-save-${v.id}`} title="Enregistrer"
                          className="w-7 h-7 grid place-items-center border border-aurora-cyan/50 text-aurora-cyan rounded-sm hover:bg-aurora-cyan/10 transition-colors">{savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}</button>
                        <button onClick={cancelEdit} data-testid={`ecu-cancel-${v.id}`} title="Annuler"
                          className="w-7 h-7 grid place-items-center border border-white/15 rounded-sm hover:text-white transition-colors"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ) : (
                <tr key={v.id} data-testid={`ecu-row-${v.id}`} className={`border-b border-white/5 ${!v.validated ? "bg-signal-processing/5" : ""}`}>
                  <td className="px-3 py-2 text-white font-medium">{v.brand}</td>
                  <td className="px-3 py-2 text-aurora-cyan font-mono text-xs">{v.hw}</td>
                  <td className="px-3 py-2 text-zinc-400 font-mono text-xs">{v.count || 1}</td>
                  <td className="px-3 py-2">
                    <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm ${v.validated ? "bg-signal-success/15 text-signal-success" : "bg-signal-processing/15 text-signal-processing"}`}>{badge(v)}</span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => startEdit(v)} data-testid={`ecu-edit-${v.id}`} title="Modifier"
                        className="w-7 h-7 grid place-items-center border border-white/15 text-zinc-400 rounded-sm hover:border-aurora-cyan hover:text-aurora-cyan transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                      {!v.validated && (
                        <button onClick={() => validate(v.id)} data-testid={`ecu-validate-${v.id}`} title="Valider"
                          className="w-7 h-7 grid place-items-center border border-signal-success/40 text-signal-success rounded-sm hover:bg-signal-success/10 transition-colors"><Check className="w-3.5 h-3.5" /></button>
                      )}
                      <button onClick={() => del(v.id)} data-testid={`ecu-del-${v.id}`} title="Supprimer"
                        className="w-7 h-7 grid place-items-center border border-white/15 rounded-sm hover:border-signal-error hover:text-signal-error transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
                )
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center font-mono text-xs text-zinc-600">Aucun ECU</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
